<template>
  <div class="pag-activacion">
    <!-- CABECERA -->
    <div class="cabecera">
      <div class="cabecera__izquierda">
        <div class="cabecera__icono">
          <i class="bi bi-toggles"></i>
        </div>
        <div>
          <h1 class="cabecera__titulo">Activación de Módulos</h1>
          <p class="cabecera__subtitulo">Controla qué módulos están disponibles globalmente en el sistema</p>
        </div>
      </div>
      <button class="btn-secundario" @click="cargarModulos" :disabled="cargando">
        <i class="bi bi-arrow-clockwise" :class="{ 'girando': cargando }"></i>
        Actualizar
      </button>
    </div>

    <!-- ESTADÍSTICAS -->
    <div class="estadisticas" v-if="estadisticas">
      <div class="estadistica">
        <span class="estadistica__numero">{{ estadisticas.total }}</span>
        <span class="estadistica__etiqueta">Total</span>
      </div>
      <div class="estadistica estadistica--activo">
        <span class="estadistica__numero">{{ estadisticas.activos }}</span>
        <span class="estadistica__etiqueta">Activos</span>
      </div>
      <div class="estadistica estadistica--inactivo">
        <span class="estadistica__numero">{{ estadisticas.inactivos }}</span>
        <span class="estadistica__etiqueta">Inactivos</span>
      </div>
    </div>

    <!-- FILTROS -->
    <div class="filtros">
      <div class="filtros__busqueda">
        <i class="bi bi-search filtros__icono"></i>
        <input
          v-model="busqueda"
          @input="buscarConDebounce"
          type="text"
          placeholder="Buscar módulo..."
          class="filtros__campo"
        />
      </div>
      <select v-model="filtroEstado" class="filtros__select">
        <option value="">Todos los estados</option>
        <option value="1">Solo activos</option>
        <option value="0">Solo inactivos</option>
      </select>
    </div>

    <!-- ESTADO: CARGANDO -->
    <div v-if="cargando" class="estado-carga">
      <div class="spinner"></div>
      <span>Cargando módulos...</span>
    </div>

    <!-- ESTADO: ERROR -->
    <div v-else-if="error" class="estado-error">
      <i class="bi bi-exclamation-triangle-fill"></i>
      {{ error }}
    </div>

    <!-- ESTADO: VACÍO -->
    <div v-else-if="!modulosFiltrados.length" class="estado-vacio">
      <i class="bi bi-toggles estado-vacio__icono"></i>
      <p>No se encontraron módulos con esos filtros</p>
    </div>

    <!-- CUADRÍCULA DE MÓDULOS -->
    <div v-else class="cuadricula">
      <div
        v-for="modulo in modulosFiltrados"
        :key="modulo.id"
        class="tarjeta"
        :class="{ 'tarjeta--inactiva': !modulo.is_active }"
      >
        <div class="tarjeta__cabecera">
          <div class="tarjeta__info">
            <span class="tarjeta__codigo">{{ modulo.code }}</span>
            <h3 class="tarjeta__nombre">{{ modulo.name }}</h3>
          </div>
          <div class="tarjeta__interruptor-wrap">
            <label class="interruptor" :title="modulo.is_active ? 'Desactivar módulo' : 'Activar módulo'">
              <input
                type="checkbox"
                :checked="modulo.is_active"
                :disabled="actualizando === modulo.id"
                @change="cambiarEstado(modulo)"
              />
              <span class="interruptor__palanca"></span>
            </label>
            <span class="tarjeta__estado-texto" :class="modulo.is_active ? 'texto-activo' : 'texto-inactivo'">
              {{ modulo.is_active ? 'Activo' : 'Inactivo' }}
            </span>
          </div>
        </div>

        <p class="tarjeta__descripcion">{{ modulo.description || 'Sin descripción' }}</p>

        <div class="tarjeta__pie">
          <span
            class="etiqueta"
            :class="modulo.is_main ? 'etiqueta--principal' : 'etiqueta--secundario'"
          >
            {{ modulo.is_main ? 'Principal' : 'Sub-módulo' }}
          </span>
        </div>

        <!-- Overlay de carga -->
        <div v-if="actualizando === modulo.id" class="tarjeta__cargando">
          <div class="spinner spinner--blanco"></div>
        </div>
      </div>
    </div>

    <!-- TOTAL -->
    <p v-if="modulosFiltrados.length" class="total">
      Mostrando {{ modulosFiltrados.length }} de {{ modulos.length }} módulos
    </p>
  </div>
</template>

<script>
import api from '@/services/api'
import notificationService from '@/services/notificationService'

export default {
  name: 'ModulosActivacion',

  data() {
    return {
      modulos: [],
      estadisticas: null,
      cargando: false,
      actualizando: null,
      error: '',
      busqueda: '',
      filtroEstado: '',
      temporizadorBusqueda: null,
    }
  },

  computed: {
    modulosFiltrados() {
      let lista = this.modulos
      if (this.busqueda.trim()) {
        const q = this.busqueda.toLowerCase()
        lista = lista.filter(m =>
          m.name?.toLowerCase().includes(q) ||
          m.code?.toLowerCase().includes(q) ||
          m.description?.toLowerCase().includes(q)
        )
      }
      if (this.filtroEstado !== '') {
        const activo = this.filtroEstado === '1'
        lista = lista.filter(m => Boolean(m.is_active) === activo)
      }
      return lista
    },
  },

  mounted() {
    this.cargarModulos()
  },

  methods: {
    buscarConDebounce() {
      clearTimeout(this.temporizadorBusqueda)
      this.temporizadorBusqueda = setTimeout(() => {}, 0) // reactivo via computed
    },

    async cargarModulos() {
      this.cargando = true
      this.error = ''
      try {
        const resp = await api.get('/registro-empresas/modulos-disponibles?per_page=200')
        if (resp.data && resp.data.data) {
          this.modulos = resp.data.data
          this.calcularEstadisticas()
        }
      } catch (e) {
        this.error = 'Error al cargar los módulos'
        console.error('[ModulosActivacion]', e)
      } finally {
        this.cargando = false
      }
    },

    calcularEstadisticas() {
      this.estadisticas = {
        total: this.modulos.length,
        activos: this.modulos.filter(m => m.is_active).length,
        inactivos: this.modulos.filter(m => !m.is_active).length,
      }
    },

    async cambiarEstado(modulo) {
      this.actualizando = modulo.id
      const nuevoEstado = !modulo.is_active
      try {
        const resp = await api.put(`/registro-empresas/modulos-disponibles/${modulo.id}`, {
          is_active: nuevoEstado,
        })
        if (resp.data && resp.data.success) {
          modulo.is_active = nuevoEstado
          this.calcularEstadisticas()
          notificationService.success(nuevoEstado ? 'Módulo activado' : 'Módulo desactivado')
        } else {
          notificationService.error('Error al actualizar el módulo')
        }
      } catch (e) {
        console.error('[ModulosActivacion]', e)
        notificationService.error('Error al actualizar el módulo')
      } finally {
        this.actualizando = null
      }
    },
  },
}
</script>

<style scoped>
.pag-activacion {
  --primario: var(--primary-main, #0ea5e9);
  --primario-hover: var(--primary-hover, #0284c7);
  --fondo: var(--bg-main, #f8fafc);
  --fondo-tarjeta: var(--secondary-bg-main, #ffffff);
  --texto: var(--text-primary, #0f172a);
  --texto-sec: var(--text-secondary, #475569);
  --texto-mut: var(--text-muted, #94a3b8);
  --borde: var(--border-main, #e2e8f0);
  --borde-foco: var(--border-focus, #0ea5e9);
  --exito: var(--accent-success, #10b981);
  --alerta: var(--accent-warning, #f59e0b);
  --peligro: var(--accent-danger, #ef4444);
  --sombra-sm: var(--shadow-sm, 0 1px 2px 0 rgb(0 0 0 / 0.05));
  --sombra-md: var(--shadow-md, 0 4px 6px -1px rgb(0 0 0 / 0.1));
  min-height: 100vh;
  padding: 1.5rem;
  background: var(--fondo);
  color: var(--texto);
}

/* CABECERA */
.cabecera { display: flex; justify-content: space-between; align-items: center; gap: 1rem; margin-bottom: 1.5rem; flex-wrap: wrap; }
.cabecera__izquierda { display: flex; align-items: center; gap: 0.875rem; }
.cabecera__icono { width: 2.75rem; height: 2.75rem; border-radius: 10px; background: var(--primario); color: #fff; display: flex; align-items: center; justify-content: center; font-size: 1.25rem; flex-shrink: 0; }
.cabecera__titulo { margin: 0; font-size: 1.375rem; font-weight: 700; color: var(--texto); line-height: 1.2; }
.cabecera__subtitulo { margin: 0.125rem 0 0; font-size: 0.8125rem; color: var(--texto-sec); }

/* BOTONES */
.btn-secundario { display: inline-flex; align-items: center; gap: 0.375rem; padding: 0.5rem 1rem; background: var(--fondo-tarjeta); color: var(--texto-sec); border: 1px solid var(--borde); border-radius: 8px; font-size: 0.875rem; font-weight: 500; cursor: pointer; transition: background 0.15s, color 0.15s; }
.btn-secundario:hover:not(:disabled) { color: var(--texto); background: var(--borde); }

/* ESTADÍSTICAS */
.estadisticas { display: flex; gap: 0.75rem; margin-bottom: 1.25rem; flex-wrap: wrap; }
.estadistica { display: flex; flex-direction: column; align-items: center; padding: 0.625rem 1rem; background: var(--fondo-tarjeta); border: 1px solid var(--borde); border-radius: 8px; min-width: 80px; box-shadow: var(--sombra-sm); }
.estadistica__numero { font-size: 1.375rem; font-weight: 700; color: var(--texto); line-height: 1; }
.estadistica__etiqueta { font-size: 0.6875rem; color: var(--texto-mut); text-transform: uppercase; letter-spacing: 0.04em; margin-top: 0.25rem; }
.estadistica--activo .estadistica__numero { color: var(--exito); }
.estadistica--inactivo .estadistica__numero { color: var(--alerta); }

/* FILTROS */
.filtros { display: flex; gap: 0.75rem; margin-bottom: 1.25rem; flex-wrap: wrap; align-items: center; }
.filtros__busqueda { position: relative; flex: 1; min-width: 220px; max-width: 360px; }
.filtros__icono { position: absolute; left: 0.75rem; top: 50%; transform: translateY(-50%); color: var(--texto-mut); pointer-events: none; }
.filtros__campo { width: 100%; padding: 0.5rem 0.875rem 0.5rem 2.25rem; border: 1px solid var(--borde); border-radius: 8px; background: var(--fondo-tarjeta); color: var(--texto); font-size: 0.875rem; }
.filtros__campo:focus { outline: none; border-color: var(--borde-foco); box-shadow: 0 0 0 3px rgb(14 165 233 / 0.15); }
.filtros__campo::placeholder { color: var(--texto-mut); }
.filtros__select { padding: 0.5rem 0.875rem; border: 1px solid var(--borde); border-radius: 8px; background: var(--fondo-tarjeta); color: var(--texto); font-size: 0.875rem; cursor: pointer; }
.filtros__select:focus { outline: none; border-color: var(--borde-foco); }

/* ESTADOS */
.estado-carga { display: flex; align-items: center; justify-content: center; gap: 0.75rem; padding: 3rem; color: var(--texto-sec); }
.estado-error { display: flex; align-items: center; gap: 0.625rem; padding: 1rem 1.25rem; background: rgb(239 68 68 / 0.08); border: 1px solid var(--peligro); border-radius: 8px; color: var(--peligro); font-size: 0.875rem; }
.estado-vacio { text-align: center; padding: 4rem 2rem; color: var(--texto-sec); }
.estado-vacio__icono { font-size: 3rem; color: var(--texto-mut); display: block; margin-bottom: 1rem; }

/* CUADRÍCULA */
.cuadricula { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 1rem; margin-bottom: 1rem; }

/* TARJETA */
.tarjeta { background: var(--fondo-tarjeta); border: 1px solid var(--borde); border-radius: 10px; padding: 1rem; box-shadow: var(--sombra-sm); transition: box-shadow 0.15s, opacity 0.2s; position: relative; overflow: hidden; }
.tarjeta:hover { box-shadow: var(--sombra-md); }
.tarjeta--inactiva { opacity: 0.65; }

.tarjeta__cabecera { display: flex; justify-content: space-between; align-items: flex-start; gap: 0.75rem; margin-bottom: 0.625rem; }
.tarjeta__info { min-width: 0; }
.tarjeta__codigo { font-size: 0.6875rem; font-family: monospace; color: var(--primario); font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; }
.tarjeta__nombre { margin: 0.125rem 0 0; font-size: 0.9375rem; font-weight: 600; color: var(--texto); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.tarjeta__interruptor-wrap { display: flex; flex-direction: column; align-items: center; gap: 0.25rem; flex-shrink: 0; }
.tarjeta__estado-texto { font-size: 0.6875rem; font-weight: 500; }
.texto-activo { color: var(--exito); }
.texto-inactivo { color: var(--texto-mut); }

.tarjeta__descripcion { font-size: 0.8125rem; color: var(--texto-sec); line-height: 1.5; margin-bottom: 0.75rem; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }

.tarjeta__pie { display: flex; gap: 0.5rem; }

/* ETIQUETAS */
.etiqueta { display: inline-block; padding: 0.1875rem 0.5rem; border-radius: 20px; font-size: 0.6875rem; font-weight: 500; }
.etiqueta--principal { background: rgb(14 165 233 / 0.12); color: var(--primario); }
.etiqueta--secundario { background: rgb(148 163 184 / 0.15); color: var(--texto-sec); }

/* OVERLAY DE CARGA */
.tarjeta__cargando { position: absolute; inset: 0; background: rgb(255 255 255 / 0.75); display: flex; align-items: center; justify-content: center; border-radius: 10px; }

/* SPINNER */
.spinner { width: 1.5rem; height: 1.5rem; border: 2px solid var(--borde); border-top-color: var(--primario); border-radius: 50%; animation: girar 0.7s linear infinite; }
.spinner--blanco { border-color: rgb(255 255 255 / 0.3); border-top-color: var(--primario); }

/* INTERRUPTOR */
.interruptor { position: relative; display: inline-flex; cursor: pointer; }
.interruptor input { opacity: 0; width: 0; height: 0; position: absolute; }
.interruptor__palanca { display: inline-block; width: 2.25rem; height: 1.25rem; background: var(--texto-mut); border-radius: 1rem; transition: background 0.2s; position: relative; }
.interruptor__palanca::after { content: ''; position: absolute; top: 0.1875rem; left: 0.1875rem; width: 0.875rem; height: 0.875rem; background: #fff; border-radius: 50%; transition: transform 0.2s; }
.interruptor input:checked + .interruptor__palanca { background: var(--exito); }
.interruptor input:checked + .interruptor__palanca::after { transform: translateX(1rem); }
.interruptor input:disabled + .interruptor__palanca { opacity: 0.5; cursor: not-allowed; }

/* TOTAL */
.total { text-align: center; font-size: 0.8125rem; color: var(--texto-mut); margin-top: 0.5rem; }

/* ANIMACIONES */
@keyframes girar { to { transform: rotate(360deg); } }
.girando { animation: girar 0.7s linear infinite; }

/* RESPONSIVE */
@media (max-width: 640px) {
  .pag-activacion { padding: 1rem; }
  .cabecera { flex-direction: column; align-items: flex-start; }
  .cuadricula { grid-template-columns: 1fr; }
}
</style>
